import { useLang } from '../i18n'
import type { KbSourceSyncStatus } from '../lib/api'

/** A run whose heartbeat is older than this reads as interrupted (mirrors domain/kb-sync-status.ts). */
const STALE_RUN_MS = 10 * 60_000

/** True when nothing has written to this run for STALE_RUN_MS — the process died, quietly. */
export function isRunStale(heartbeatAt: string, now = Date.now()): boolean {
  const beat = Date.parse(heartbeatAt)
  if (Number.isNaN(beat)) return true
  return now - beat > STALE_RUN_MS
}

/** True while a run is genuinely moving — what decides whether the table polls at all. */
export function isRunLive(status: KbSourceSyncStatus): boolean {
  const run = status.run
  if (!run) return false
  if (run.phase === 'done' || run.phase === 'failed') return false
  return !isRunStale(run.heartbeatAt)
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
}

/**
 * What one source's indexation is doing right now, or when it last ran.
 *
 * Runs are launched by the `kb-sync-corporate` job, a different process from the API, so
 * everything here comes from a DATABASE row the pipeline writes as it goes — not from any in-memory
 * job the backend happens to be holding. That is also why "interrupted" exists as a state: a killed
 * run leaves a row that would otherwise read as "indexing" forever, and a stale heartbeat is the
 * only way to tell.
 *
 * The progress bar carries a real `aria-valuenow` only once the crawl has returned a denominator.
 * While crawling there is none — a crawl writes nothing for 5-13 minutes — so it is deliberately
 * INDETERMINATE (no aria-valuenow, an `aria-valuetext` saying so) rather than showing an invented
 * percentage. A progress bar that lies is worse than one that admits it does not know yet.
 */
export function KbSourceSyncCell({ status }: { status: KbSourceSyncStatus }) {
  const { t } = useLang()
  const run = status.run
  const stale = run ? isRunStale(run.heartbeatAt) : false
  const settled = !run || run.phase === 'done' || run.phase === 'failed'

  if (run && !settled && stale) {
    return (
      <span className="kbsrc-state kbsrc-state-warn" role="status" aria-live="polite">
        {t('kbsrc_interrupted')}
      </span>
    )
  }

  if (run && run.phase === 'crawling') {
    return (
      <div className="kbsrc-run" role="status" aria-live="polite">
        <div className="spb-progress-wrap">
          <div
            className="spb-progress-bar kbsrc-progress-indeterminate"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            // No aria-valuenow on purpose: the crawl has not reported a total yet, so there is no
            // honest percentage to give. `aria-valuetext` says what is happening instead.
            aria-valuetext={t('kbsrc_crawling')}
            aria-label={t('kbsrc_progress_label')}
          />
        </div>
        <span className="kbsrc-state">{t('kbsrc_crawling')}</span>
      </div>
    )
  }

  if (run && run.phase === 'indexing') {
    const total = run.totalFiles ?? 0
    const pct = total > 0 ? Math.min(100, Math.round((run.processed / total) * 100)) : 0
    return (
      <div className="kbsrc-run" role="status" aria-live="polite">
        <div className="spb-progress-wrap">
          <div
            className="spb-progress-bar"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('kbsrc_progress_label')}
          />
        </div>
        <span className="kbsrc-state">
          {t('kbsrc_indexing')
            .replace('{done}', String(run.processed))
            .replace('{total}', String(run.totalFiles ?? '?'))}
        </span>
      </div>
    )
  }

  if (run && run.phase === 'failed') {
    return (
      <span className="kbsrc-state kbsrc-state-warn" title={run.error ?? undefined}>
        {t('kbsrc_run_failed')}
      </span>
    )
  }

  // "Last indexed" is when the source's last run COMPLETED — failed files and all. Read from the
  // sealed-token date instead, a library where a handful of documents fail every pass would look
  // weeks stale while it is in fact indexed nightly (the token only advances on a spotless pass).
  const last = formatDate(status.lastIndexedAt)
  return (
    <span className="kbsrc-state kbsrc-muted" title={run?.error ?? undefined}>
      {last || t('kbsrc_never_indexed')}
    </span>
  )
}
